import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../utils/seo';

// ===== Associate Butler (EN) full modernization — Implementer B scope =====
// Design canon: src/pages/about/overview/page.tsx & home (navy #1a2942 / brass #a38e65 / gold #d7b779 /
//   BTN-08 two states / gold eyebrow -> gold-accent heading -> brass rule / 3+ line leads / no ornamental dividers).
// Factual canon (no fabrication, prices unchanged, EN = no Japanese characters):
//   [S-cur] prior src/pages/en/qualifications/associate-butler/page.tsx (10 chapters, ¥48,000, exam spec)
//   [S-overview] src/pages/about/overview/page.tsx (BTN08, section grammar)

const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

const curriculum = [
  { n: '01', t: 'Butler History and Culture', d: 'Learn the development of the butler profession from its origins to modern times. Understand butler roles in European aristocratic society, cultural differences worldwide, and the position of butlers in Japan to grasp the essence of values butlers have cherished.' },
  { n: '02', t: 'Butler Mindset and Philosophy', d: 'Deeply explore hospitality and the spirit of service. Learn the values and professional attitudes butlers cherish, including consideration from others’ perspectives, anticipatory care, and behind-the-scenes preparation.' },
  { n: '03', t: 'Basic Manners and Etiquette', d: 'Learn fundamental social manners from a butler’s perspective. Master proper posture, types of bows and their usage, polite language, and public etiquette—content immediately applicable to daily life.' },
  { n: '04', t: 'Grooming and Impression Management', d: 'Understand the importance of first impressions and learn appropriate attire selection and maintaining cleanliness. Master butler aesthetics of attention to detail, including TPO-appropriate dress, hair and nail care, and fragrance consideration.' },
  { n: '05', t: 'Communication Fundamentals', d: 'Learn communication techniques that create positive impressions. Master active listening, maintaining appropriate distance, non-verbal communication (expressions, posture, eye contact), and reporting basics for smooth relationships.' },
  { n: '06', t: 'Table Manners Basics', d: 'Learn proper dining etiquette. Master Western and Japanese table manners, correct use of cutlery and glassware, and graceful dining behavior to confidently handle formal occasions.' },
  { n: '07', t: 'Basic Customer Service', d: 'Learn customer service fundamentals from a butler’s perspective, including guest reception, phone etiquette, guidance and escort, and farewell protocols. Master professional service through thoughtful word choice, smooth movements, and detailed consideration.' },
  { n: '08', t: 'Daily Attentiveness', d: 'Learn environmental maintenance basics, organizational thinking, seasonal considerations, and developing observational skills. Understand the essence of butler “invisible work” through noticing small changes and maintaining comfortable environments.' },
  { n: '09', t: 'Ceremonial Occasion Manners', d: 'Learn manners for important life events including weddings, funerals, celebrations, and gift-giving. Master appropriate behavior, proper attire, and gift selection for each occasion—essential social etiquette.' },
  { n: '10', t: 'Comprehensive Practice', d: 'Review learned content through practical case studies. Solidify knowledge through scenario-based response simulations and comprehensive review questions. An important chapter for exam preparation.' },
];

const recommended = [
  'Service industry professionals seeking skill enhancement',
  'Those wanting dignified behavior in customer service or sales',
  'Those wanting to learn proper manners for formal occasions',
  'Those wanting to improve business etiquette',
  'Those interested in the butler profession',
  'Those seeking self-improvement and cultural enrichment',
  'Those wanting to enhance hospitality awareness in daily life',
  'Those aiming for Professional or Master Butler certification',
];

const materials = [
  { t: 'Textbook (approx. 100 pages)', d: 'A dedicated textbook explaining butler fundamentals in detail. Rich with illustrations and diagrams, easy to understand for beginners.' },
  { t: 'Online Video Lessons', d: 'Clear video explanations including practical skills. Bowing techniques, table manners practice, and more—content only videos can convey. One-year viewing period.' },
  { t: 'Practice Kit', d: 'Practice materials to support your learning, so you can rehearse practical skills at home.' },
  { t: 'Certification Exam & Answer Sheet', d: 'Exam questions you can complete at home. Take your time to answer thoughtfully; our association grades your submission.' },
];

const examRows = [
  ['Format', 'Written exam (essay and multiple choice)'],
  ['Passing Score', '80 points or higher'],
  ['Deadline', 'Submit within 1 year of enrollment'],
  ['Eligibility', 'No restrictions (anyone can apply)'],
  ['Location', 'At home (distance exam)'],
  ['Retake', 'Unlimited retakes with a ¥15,000 retake fee'],
];

const ladder = [
  { key: 'associate', roman: 'I', name: 'Associate Butler', level: 'Beginner', price: '48,000', href: '/en/qualifications/associate-butler' },
  { key: 'professional', roman: 'II', name: 'Professional Butler', level: 'Intermediate', price: '78,000', href: '/en/qualifications/professional-butler' },
  { key: 'master', roman: 'III', name: 'Master Butler', level: 'Advanced', price: '128,000', href: '/en/qualifications/master-butler' },
];

function SecHead({ roman, en, children }: { roman: string; en: string; children: React.ReactNode }) {
  return (
    <div className="mb-9">
      <div className="-mx-5 sm:-mx-8 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 py-[18px]">
        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">{roman}. {en}</span>
      </div>
      <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.45] tracking-[0.01em]">{children}</h2>
    </div>
  );
}

export default function EnAssociateButlerPage() {
  useEffect(() => {
    updateSEO({
      title: 'Associate Butler Certification (Beginner) | Japan Butler Association',
      description: 'Begin your butler career with the Associate Butler certification from the Japan Butler Association. Learn fundamental manners, hospitality, and communication across 10 chapters. Study at home in 2–4 months for ¥48,000, with a complimentary 3-hour butler salon experience (¥30,000 value).',
      keywords: 'associate butler, butler certification, entry level, beginner butler, Japan Butler Association',
      canonical: 'https://butler-association.jp/en/qualifications/associate-butler',
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Certifications', href: '/en/qualifications' },
    { label: 'Associate Butler' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-fs-en-026-qualifications_associate-butler-v2.webp"
          alt="Associate Butler foundational training session"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_48%,rgba(16,20,26,0.36)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-5 sm:px-8 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Associate Butler · Level I</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="font-['Playfair_Display',serif] font-medium text-white text-[clamp(30px,3.6vw,46px)] leading-[1.25] tracking-[0.01em] mb-3">
              Your first step, <span className="text-[#d7b779]">Associate Butler</span>
            </h1>
            <p className="font-['Playfair_Display',serif] italic text-[#e7e2d8] text-sm tracking-[0.08em] mb-6">Beginner Level</p>
            <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.9] mb-8">
              An entry-level certification that teaches fundamental butler knowledge and culture. Learn the essence of hospitality, proper manners, and the spirit of service from a butler’s perspective—skills valuable across all service industries, business, and daily life. Perfect for beginners with no prior experience.
            </p>
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
              <div>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[10px] text-[#d7b779] mb-1">Fee</span>
                <span className="font-['Playfair_Display',serif] font-medium text-white text-[28px] leading-none">&yen;48,000</span>
                <span className="block font-['Playfair_Display',serif] text-[11px] text-white/60 mt-1">Tax &amp; shipping included</span>
              </div>
              <a href="/en/contact" className={BTN08_ON_NAVY}>Enroll Now</a>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb: placed right after the billboard hero (Yuichi 2026-07-17 note 2). Override shared Breadcrumb's header-clearance top padding via child selector */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14 sm:py-18 lg:py-20 space-y-16 sm:space-y-20">

          <section>
            <SecHead roman="I" en="About This Certification">Butler fundamentals, <span className="text-[#a38e65]">learned systematically</span></SecHead>
            <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl">
              <p>The Associate Butler certification teaches dignified behaviour and the essence of hospitality through the fundamentals of the butler profession. Starting with butler history and roles, you will systematically learn practical manners, communication techniques, and the spirit of service applicable to daily life and the service industry.</p>
              <p>Designed for beginners, you can study at home at your own pace. Through effective learning that combines textbook materials with online video lessons, you will be ready to take the exam in approximately two to four months.</p>
              <p>It is ideal for those wishing to strengthen butler fundamentals, improve customer-service skills, learn manners for formal occasions, or refine business etiquette—a confident first step into the profession.</p>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              {[['Fee', '¥48,000'], ['Textbook', '~100 pp.'], ['Study', '2–4 mo.'], ['Prerequisite', 'None']].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-['Playfair_Display',serif] uppercase tracking-[0.08em] text-[11px] text-[#8f7a56] mb-1.5">{k}</dt>
                  <dd className="font-['Playfair_Display',serif] font-medium text-2xl text-[#1a2942]">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <SecHead roman="II" en="Why Associate">Start with confidence, <span className="text-[#a38e65]">no experience needed</span></SecHead>
            <div className="grid md:grid-cols-3 gap-x-10 gap-y-8">
              {[
                { no: '01', ja: 'Beginners Welcome', d: 'No special qualifications are required. The curriculum builds carefully from the very basics, even for those new to the butler world.' },
                { no: '02', ja: 'Study at Home', d: 'As a distance-learning programme, it is available nationwide. Learn at your own pace while balancing work and family.' },
                { no: '03', ja: 'Practical Content', d: 'Butler refinement is directly useful in everyday life and business. The content is practical and applicable from day one.' },
              ].map((f) => (
                <div key={f.no} className="border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none block mb-3">{f.no}</span>
                  <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl mb-2.5 leading-[1.4]">{f.ja}</h3>
                  <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <SecHead roman="III" en="Curriculum · 10 Chapters">Ten chapters of <span className="text-[#a38e65]">butler foundations</span></SecHead>
            <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl mb-8">
              The Associate Butler certification systematically covers 10 fundamental areas. Each chapter is built around practical content, so you acquire not only knowledge but the actual behaviour and techniques of a butler.
            </p>
            <img
              src="/images/generated/jba-fs-en-027-qualifications_associate-butler.webp"
              alt="Associate Butler study materials: textbook and practice kit"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.n} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.n}</span>
                  <div>
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-lg mb-2.5 leading-[1.4]">{c.t}</h3>
                    <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* IV. Recommended (fine-rule two-column list) = new variation (a). Origin: overview S2 definition-list grammar */}
          <section>
            <SecHead roman="IV" en="Recommended For">This certification is <span className="text-[#a38e65]">ideal for</span></SecHead>
            <ul className="grid sm:grid-cols-2 gap-x-10 border-t-2 border-[#1a2942]">
              {recommended.map((r) => (
                <li key={r} className="flex items-start gap-3 border-b border-[#e2ddd2] py-4">
                  <span className="text-[#a38e65] text-[13px] leading-none pt-1.5" aria-hidden="true">◆</span>
                  <span className="font-['Playfair_Display',serif] text-[15px] text-[#1a2942] leading-[1.7]">{r}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* V. Materials (stacked split cards) = new variation (b). Origin: catalog SEC-N04 */}
          <section>
            <SecHead roman="V" en="Course Materials">Everything you need, <span className="text-[#a38e65]">delivered</span></SecHead>
            <div className="grid md:grid-cols-2 gap-6">
              {materials.map((m, i) => (
                <div key={m.t} className="flex gap-5 bg-[#f8f6f0] border border-[#a38e65]/25 p-6">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl leading-none shrink-0 pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-base sm:text-lg mb-2 leading-[1.4]">{m.t}</h3>
                    <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{m.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-[#1a2942] px-7 sm:px-10 py-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-7">
              <span className="font-['Playfair_Display',serif] italic text-[#d7b779] text-sm tracking-[0.06em] shrink-0">Special Bonus</span>
              <div>
                <h3 className="font-['Playfair_Display',serif] font-medium text-[#f8f6f0] text-lg mb-1">3-Hour Butler Salon Experience Ticket</h3>
                <p className="font-['Playfair_Display',serif] text-sm text-[#ded8cc] leading-[1.85]">A special ticket to experience real butler service at our Odaiba salon (¥30,000 value), deepening your certification learning.</p>
              </div>
            </div>
          </section>

          <section>
            <SecHead roman="VI" en="Certification Exam">The exam is taken <span className="text-[#a38e65]">at home</span></SecHead>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <dl className="border-t-2 border-[#1a2942]">
                {examRows.map(([label, value]) => (
                  <div key={label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[120px] sm:shrink-0 font-['Playfair_Display',serif] uppercase tracking-[0.06em] text-[13px] text-[#8f7a56] pt-0.5">{label}</dt>
                    <dd className="flex-1 font-['Playfair_Display',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.8]">{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="lg:pt-1">
                <img
                  src="/images/generated/jba-p-018-qual-homestudy.webp"
                  alt="Distance certification exam completed at home"
                  className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30 mb-4"
                />
                <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">
                  Once your materials arrive, study at your own pace and take the exam when ready. Return your answer sheet; our association grades it, and a score of 80 or higher earns your certificate and card.
                </p>
              </div>
            </div>
          </section>

          {/* VII. Three-tier ladder (current highlighted) = new variation (c). Origin: overview S5 timeline -> qualification stepper */}
          <section>
            <SecHead roman="VII" en="The Three Tiers">Advance through <span className="text-[#a38e65]">three tiers</span></SecHead>
            <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl mb-8">
              Butler certification is earned step by step—from Associate Butler (Beginner) to Professional Butler (Intermediate) and Master Butler (Advanced). Level-skipping is not permitted. A solid Associate Butler foundation opens the path ahead.
            </p>
            <ol className="border-t border-[#e2ddd2]">
              {ladder.map((t) => {
                const current = t.key === 'associate';
                return (
                  <li key={t.key} className={current ? 'bg-[#1a2942] border-b border-[#e2ddd2]' : 'border-b border-[#e2ddd2]'}>
                    <a href={t.href} className={`flex flex-wrap items-center gap-x-6 gap-y-2 px-5 sm:px-7 py-5 group ${current ? '' : 'hover:bg-[#f8f6f0]'} transition-colors duration-300`}>
                      <span className={`font-['Playfair_Display',serif] text-2xl w-8 shrink-0 ${current ? 'text-[#d7b779]' : 'text-[#a38e65]'}`}>{t.roman}</span>
                      <div className="min-w-[220px]">
                        <span className={`block font-['Playfair_Display',serif] font-medium text-lg ${current ? 'text-[#f8f6f0]' : 'text-[#1a2942]'}`}>{t.name}{current && <span className="ml-3 align-middle text-[10px] tracking-[0.1em] text-[#1a2942] bg-[#d7b779] px-2 py-0.5">CURRENT PAGE</span>}</span>
                        <span className={`block font-['Playfair_Display',serif] italic text-xs mt-0.5 ${current ? 'text-[#c9c2b4]' : 'text-[#8f7a56]'}`}>{t.level}</span>
                      </div>
                      <span className={`font-['Playfair_Display',serif] font-medium text-xl ml-auto ${current ? 'text-[#d7b779]' : 'text-[#1a2942]'}`}>&yen;{t.price}</span>
                      <span className={`font-['Playfair_Display',serif] text-sm shrink-0 ${current ? 'text-[#d7b779]' : 'text-[#a38e65] group-hover:translate-x-1 transition-transform'}`}>{current ? '—' : 'View →'}</span>
                    </a>
                  </li>
                );
              })}
            </ol>
          </section>

          <section>
            <SecHead roman="VIII" en="Enrollment">Enrollment</SecHead>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <div className="flex flex-wrap items-end gap-x-8 gap-y-3 mb-8 pb-8 border-b border-white/15">
                <div>
                  <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[11px] text-[#d7b779] mb-1.5">Associate Butler · Fee</span>
                  <span className="font-['Playfair_Display',serif] font-medium text-white text-[34px] sm:text-[40px] leading-none">&yen;48,000</span>
                  <span className="block font-['Playfair_Display',serif] text-xs text-white/60 mt-1.5">Tax &amp; shipping included · No certification expiry</span>
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 flex-1 min-w-[260px]">
                  {['Textbook (approx. 100 pages)', 'Online video lessons (1 year)', 'Practice kit', 'Exam & answer sheet', 'Certificate & ID card', '3-hour butler salon experience'].map((x) => (
                    <li key={x} className="flex items-start gap-2 font-['Playfair_Display',serif] text-[13px] text-[#e7e2d8] leading-[1.6]">
                      <span className="text-[#d7b779] text-[11px] pt-1" aria-hidden="true">◆</span>{x}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="font-['Playfair_Display',serif] text-base text-[#e7e2d8] leading-[1.9] max-w-3xl mb-8">
                Flexible distance learning for busy professionals. Learn butler fundamentals systematically and bring dignified refinement into your everyday life. Please feel free to contact us with any questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/en/contact" className={BTN08_ON_NAVY}>Enroll or Ask a Question</a>
                <a href="/en/qualifications" className={BTN08_ON_NAVY}>Back to All Certifications</a>
              </div>
            </div>
            <p className="font-['Playfair_Display',serif] text-xs text-[#8f7a56] leading-[1.85] mt-6 max-w-3xl">
              This is a private certification issued by the Japan Butler Association. It does not guarantee employment, career advancement, or income; it is a learning programme for knowledge acquisition and self-improvement. Actual employment is subject to each employer’s hiring criteria.
            </p>
          </section>

        </div>
      </div>
    </Layout>
  );
}
